import { useState } from 'react';
import { throttleToAnimationFrame } from '@utils/common';
import { SLIDE_DURATION_MS } from './_slider.style.js';

const sliderDraggingCls = "_b7nsb1yhhm";
const SLIDE_THRESHOLD_PX = 50;
/**
 * create event handlers to manage drag'n'drop of the slider, so that users can
 * swipe left/right on mobile based devices
 */
function useDragSlide(ref, {
  index,
  amountOfSlides,
  onChange
}) {
  const [isDragging, setDragging] = useState(false);
  return [isDragging, {
    onPointerDown(evt) {
      evt.preventDefault();
      const slider = ref.current;
      if (!slider || slider.classList.contains("postDrag")) {
        // avoid starting new drag'n'drop sessions if the slider is already
        // transitioning
        return;
      }

      // cache initial state
      const state = {
        startX: evt.pageX,
        startY: evt.pageY,
        currX: evt.pageX,
        currY: evt.pageY,
        interactionType: "pending"
      };
      const handlePointerMove = evt => {
        evt.preventDefault();
        state.currX = evt.pageX;
        state.currY = evt.pageY;
        if (state.interactionType === "pending") {
          if (Math.abs(state.currY - state.startY) >= 10) {
            // if user moved the pointer vertically, then assume that he is
            // scrolling using a touch based interface
            state.interactionType = "scrolling";
            return;
          }
          if (Math.abs(state.currX - state.startX) >= 5) {
            // if the user has been swiping horizontally, then assume that we
            // are handling dragging of the slider, and update styling to
            // indicate that drag'n'drop is on-going
            setDragging(true);
            state.interactionType = "dragging";
            document.documentElement.classList.add(sliderDraggingCls);
          }
        }
        updateSliderStyle();
      };
      const updateSliderStyle = throttleToAnimationFrame(() => {
        if (state.interactionType !== "dragging") {
          return;
        }
        slider.style.transform = `translateX(-${index * 100}%) translateX(${state.currX - state.startX}px)`;
      });
      const handlePointerUp = evt => {
        evt.preventDefault();
        if (state.interactionType === "dragging") {
          // once we get here, determine whether to navigate to the previous
          // or the next slide based on the offset of the cursor
          let nextIndex = index;
          const distance = state.currX - state.startX;
          if (distance > SLIDE_THRESHOLD_PX) {
            nextIndex = index - 1;
          } else if (distance < -SLIDE_THRESHOLD_PX) {
            nextIndex = index + 1;
          }

          // begin transitioning to the desired offset
          slider.classList.add("postDrag");
          // eslint-disable-next-line @typescript-eslint/no-unused-expressions
          slider.offsetWidth;
          slider.style.transform = `translateX(${-nextIndex * 100}%)`;
          setTimeout(() => {
            // reset state when the transition ends, and trigger update of the
            // current index externally
            slider.classList.remove("postDrag");

            // if we ran the slider "out of bounds" by sliding from the first
            // to the last or last to first, then reset the position of the
            // slider layer, and update index accordingly
            if (nextIndex < 0) {
              nextIndex = amountOfSlides + nextIndex;
              slider.classList.add("outOfBounds");
              // eslint-disable-next-line @typescript-eslint/no-unused-expressions
              slider.offsetWidth;
              slider.style.transform = `translateX(${-nextIndex * 100}%)`;
              // eslint-disable-next-line @typescript-eslint/no-unused-expressions
              slider.offsetWidth;
              slider.classList.remove("outOfBounds");
            } else if (nextIndex >= amountOfSlides) {
              nextIndex = nextIndex % amountOfSlides;
              slider.classList.add("outOfBounds");
              // eslint-disable-next-line @typescript-eslint/no-unused-expressions
              slider.offsetWidth;
              slider.style.transform = `translateX(${-nextIndex * 100}%)`;
              // eslint-disable-next-line @typescript-eslint/no-unused-expressions
              slider.offsetWidth;
              slider.classList.remove("outOfBounds");
            }
            onChange(nextIndex);
          }, SLIDE_DURATION_MS);
        }
        tearDown();
      };
      const tearDown = () => {
        window.removeEventListener("pointermove", handlePointerMove);
        window.removeEventListener("pointerup", handlePointerUp);
        window.removeEventListener("pointercancel", tearDown);
        state.interactionType = "terminated";
        setDragging(false);
        document.documentElement.classList.remove(sliderDraggingCls);
      };

      // bind global event listeners
      window.addEventListener("pointermove", handlePointerMove, {
        passive: false
      });
      window.addEventListener("pointerup", handlePointerUp);
      window.addEventListener("pointercancel", tearDown);
    }
  }];
}

export { sliderDraggingCls, useDragSlide };
import "./hook.drag-slide.linaria.module.css";
//# sourceMappingURL=hook.drag-slide.js.map
